import { useQuery } from "@apollo/client";
import { useCallback, useState } from "react";
import { FormattedDate, FormattedNumber, FormattedTime } from "react-intl";

import ActionIconButton from "@components/buttons/ActionIconButton";
import PrimaryButton from "@components/buttons/PrimaryButton";
import DeleteConfirmationAlert from "@components/DeleteConfirmationAlert";
import HandleQuery from "@components/HandleQuery";
import Nbsp from "@components/Nbsp";
import ExpertTablesPage from "@components/pageTypes/ExpertTablesPage";
import Table, { TableHeader } from "@components/Table";
import PageTitle from "@components/text/PageTitle";
import BodyCopy from "@components/typography/BodyCopy";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import useOpener from "@hooks/useOpener";
import {
  GetResourceFeatureTemplatesDocument,
  GetResourceFeatureTemplatesQuery,
  GetResourceFeatureTemplatesQueryVariables,
  ResourceFeatureTemplateFragment as ResourceFeatureTemplate,
  ResourceFeatureTemplateDestroyDocument,
  ResourceFeatureTemplateDestroyMutation,
  ResourceFeatureTemplateDestroyMutationVariables,
  SortDirectionEnum
} from "@typing/Generated";

const ExpertResourceFeatureTemplatesPage = () => {
  const getResourceFeatureTemplatesQuery = useQuery<
    GetResourceFeatureTemplatesQuery,
    GetResourceFeatureTemplatesQueryVariables
  >(GetResourceFeatureTemplatesDocument);

  const resourceFeatureTemplates = useNodesFromConnection(
    getResourceFeatureTemplatesQuery.data?.resourceFeatureTemplates
  );

  const tableHeaders: TableHeader<SortDirectionEnum>[] = [
    { messageKey: "models.resourceFeatureTemplate.tableHeadings.title" },
    { messageKey: "models.resourceFeatureTemplate.tableHeadings.createdAt" },
    { messageKey: "models.resourceFeatureTemplate.tableHeadings.featureCount" },
    { messageKey: "models.resourceFeatureTemplate.tableHeadings.actions" }
  ];

  const resourceFeatureTemplateDeleteOpener = useOpener();
  const [deletingResourceFeatureTemplate, setDeletingResourceFeatureTemplate] = useState<ResourceFeatureTemplate>();

  const handleResourceFeatureTemplateDelete = useCallback(
    (resourceFeatureTemplate: ResourceFeatureTemplate) => () => {
      setDeletingResourceFeatureTemplate(resourceFeatureTemplate);
      resourceFeatureTemplateDeleteOpener.open();
    },
    [resourceFeatureTemplateDeleteOpener]
  );

  const [destroyResourceFeatureTemplate, client] = useMutationWithValidation<
    ResourceFeatureTemplateDestroyMutation,
    ResourceFeatureTemplateDestroyMutationVariables
  >(ResourceFeatureTemplateDestroyDocument, "resourceFeatureTemplate", {
    update: () => {
      client.resetStore();
    }
  });

  const handleResourceFeatureTemplateDeleted = useCallback(() => {
    if (deletingResourceFeatureTemplate) {
      destroyResourceFeatureTemplate({
        id: deletingResourceFeatureTemplate.id
      });
    }
  }, [deletingResourceFeatureTemplate, destroyResourceFeatureTemplate]);

  return (
    <ExpertTablesPage helmetKey="pageTitles.expert.resourceFeatureTemplate.list">
      <PageTitle imageName="womanPlan" titleKey="pages.expert.resourceFeatureTemplates.title" />
      <div className="ion-margin-top">
        <PrimaryButton
          routerLink="/expert/resource-feature-templates/new"
          textKey="pages.expert.resourceFeatureTemplates.addNew"
        />
      </div>
      <HandleQuery query={getResourceFeatureTemplatesQuery}>
        {data => (
          <Table<SortDirectionEnum>
            fetchMore={getResourceFeatureTemplatesQuery.fetchMore}
            headerKey="models.resourceFeatureTemplate.tableHeadings"
            headers={tableHeaders}
            pageInfo={data.resourceFeatureTemplates.pageInfo}
          >
            {resourceFeatureTemplates.map(resourceFeatureTemplate => (
              <tr key={resourceFeatureTemplate.id}>
                <td>{resourceFeatureTemplate.title}</td>
                <td>
                  <BodyCopy>
                    <FormattedDate value={resourceFeatureTemplate.createdAt} />
                    <Nbsp />
                    <FormattedTime value={resourceFeatureTemplate.createdAt} />
                  </BodyCopy>
                </td>
                <td>
                  <BodyCopy>
                    <FormattedNumber value={resourceFeatureTemplate.resourceFeatures.length} />
                  </BodyCopy>
                </td>
                <td>
                  <ActionIconButton
                    ariaLabelKey="accessibility.ariaLabels.edit"
                    icon="edit"
                    routerLink={`/expert/resource-feature-templates/${resourceFeatureTemplate.id}/edit`}
                  />
                  <ActionIconButton
                    ariaLabelKey="accessibility.ariaLabels.delete"
                    icon="trash"
                    onClick={handleResourceFeatureTemplateDelete(resourceFeatureTemplate)}
                  />
                </td>
              </tr>
            ))}
          </Table>
        )}
      </HandleQuery>
      <DeleteConfirmationAlert
        deleteKey="dictionary.delete"
        messageKey={`pages.expert.resourceFeatureTemplate.delete`}
        messageValue={deletingResourceFeatureTemplate?.title}
        messageValueKey="title"
        onDelete={handleResourceFeatureTemplateDeleted}
        opener={resourceFeatureTemplateDeleteOpener}
      />
    </ExpertTablesPage>
  );
};

export default ExpertResourceFeatureTemplatesPage;
